import { fn } from 'storybook/test';

import type { Meta, StoryFn } from '@storybook/react';
import type { CheckboxButtonBarProps } from './CheckboxButtonBar';

import { CheckboxButtonBar } from './CheckboxButtonBar';

import { Periods } from 'components/Periods';

const codeString =
  '<CheckboxButtonBar checkboxes={} defaultValues={} label={} labelPosition={} required={} notValidatedText={} disableGroup={} onSetValues={} />';

// eslint-disable-next-line import/no-default-export
export default {
  title: 'Molecules/CheckboxButtonBar',
  component: CheckboxButtonBar,
  argTypes: {
    checkboxes: {
      table: {
        disable: true,
      },
    },
    defaultValues: {
      table: {
        disable: true,
      },
    },
    label: {
      control: 'text',
    },
    labelPosition: {
      control: 'radio',
      options: ['top', 'left'],
    },
    required: {
      control: 'boolean',
    },
    notValidatedText: {
      control: 'text',
    },
    disableGroup: {
      control: 'boolean',
    },
    actAsRadioButtonBar: {
      control: 'boolean',
    },
    onSetValues: {
      table: {
        disable: true,
      },
    },
  },
  parameters: {
    inspectComponent: CheckboxButtonBar,
    codeString: codeString,
  },
} as Meta<CheckboxButtonBarProps>;

const Template: StoryFn<CheckboxButtonBarProps> = (args) => <CheckboxButtonBar {...args} />;

export const CheckboxButtonBarExample = Template.bind({});
CheckboxButtonBarExample.args = {
  label: 'Periode',
  required: true,
  checkboxes: [
    {
      label: '1.1',
      value: '1',
    },
    {
      label: '1.2',
      value: '2',
    },
    {
      label: '1.3',
      value: '3',
    },
    {
      label: '4',
      value: '4',
    },
    {
      label: '5',
      value: '5',
    },
    {
      label: '6',
      value: '6',
    },
  ],
  defaultValues: ['1', '2'],
  labelPosition: 'top',
  subLabel: 'Selecteer een week',
  footerText: 'Weken in periode 2',
  onSetValues: fn(),
};

export const AsRadioButtonBar = Template.bind({});
AsRadioButtonBar.args = {
  label: 'Weken',
  required: true,
  checkboxes: [
    {
      label: '1',
      value: '1',
    },
    {
      label: '2',
      value: '2',
    },
    {
      label: '3',
      value: '3',
    },
    {
      label: '4',
      value: '4',
    },
    {
      label: '5',
      value: '5',
    },
    {
      label: '6',
      value: '6',
    },
  ],
  defaultValues: ['1'],
  labelPosition: 'top',
  actAsRadioButtonBar: true,
  onSetValues: fn(),
};

export const WithPrefix = Template.bind({});
WithPrefix.args = {
  label: 'Weken',
  required: true,
  checkboxes: [
    {
      label: '1',
      value: '1',
      prefix: '13',
    },
    {
      label: '2',
      value: '2',
      prefix: '14',
    },
    {
      label: '3',
      value: '3',
      prefix: '15',
    },
    {
      label: '4',
      value: '4',
      prefix: '16',
    },
    {
      label: '5',
      value: '5',
      prefix: '17',
    },
    {
      label: '6',
      value: '6',
      prefix: '18',
    },
  ],
  defaultValues: ['1'],
  labelPosition: 'top',
  actAsRadioButtonBar: false,
  onSetValues: fn(),
};

export const WithTooltip = Template.bind({});
WithTooltip.args = {
  label: 'Weken',
  required: true,
  checkboxes: [
    {
      label: '1',
      value: '1',
      prefix: '36',
      tooltip: (
        <>
          1 sept - 5 sept
          <br />
          2025
        </>
      ),
      tooltipPosition: 'left',
    },
    {
      label: '2',
      value: '2',
      prefix: '37',
      tooltip: (
        <>
          8 sept - 12 sept
          <br />
          2025
        </>
      ),
    },
    {
      label: '3',
      value: '3',
      prefix: '38',
      tooltip: (
        <>
          15 sept - 19 sept
          <br />
          2025
        </>
      ),
    },
    {
      label: '4',
      value: '4',
      prefix: '39',
      tooltip: (
        <>
          22 sept - 26 sept
          <br />
          2025
        </>
      ),
    },
    {
      label: '5',
      value: '5',
      prefix: '40',
      tooltip: (
        <>
          29 sept - 3 okt
          <br />
          2025
        </>
      ),
    },
    {
      label: '6',
      value: '6',
      prefix: '41',
      tooltip: (
        <>
          6 okt - 10 okt
          <br />
          2025
        </>
      ),
    },
    {
      label: '7',
      value: '7',
      prefix: '42',
      tooltip: (
        <>
          13 okt - 17 okt
          <br />
          2025
        </>
      ),
    },
    {
      label: '8',
      value: '8',
      prefix: '43',
      tooltip: (
        <>
          20 okt - 24 okt
          <br />
          2025
        </>
      ),
      tooltipPosition: 'right',
    },
  ],
  defaultValues: ['1'],
  labelPosition: 'top',
  actAsRadioButtonBar: false,
  onSetValues: fn(),
};

export const Segmented = Template.bind({});
Segmented.args = {
  label: 'Year(s)',
  required: true,
  checkboxes: [
    {
      label: '1',
      value: '1',
      tooltip: <>2023 - 2024</>,
      tooltipPosition: 'left',
    },
    {
      label: '2',
      value: '2',
      tooltip: <>2025 - 2026</>,
    },
    {
      label: '3',
      value: '3',
      tooltip: <>2026 - 2027</>,
    },
    {
      label: '4',
      value: '4',
      tooltip: <>2027 - 2028</>,
      tooltipPosition: 'right',
    },
  ],
  defaultValues: ['1'],
  labelPosition: 'top',
  actAsRadioButtonBar: false,
  segmented: true,
  onSetValues: fn(),
};

export const WithPeriods = Template.bind({});
WithPeriods.args = {
  label: 'Aanbieding',
  required: true,
  checkboxes: [
    {
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      label: <Periods periods={[1, 2]} />,
      value: '1',
    },
    {
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      label: <Periods periods={[2, 3]} />,
      value: '2',
    },
  ],
  defaultValues: ['1'],
  labelPosition: 'top',
  actAsRadioButtonBar: false,
  segmented: true,
  onSetValues: fn(),
};
